import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { getMediaUrl } from '../utils/mediaUrl';
import api from '../services/api';
import '../styles/components/HorizontalScrollText.css';

gsap.registerPlugin(ScrollTrigger);

const HorizontalScrollText = () => {
  const sectionRef = useRef(null);
  const line1Ref = useRef(null);
  const line2Ref = useRef(null);
  const line3Ref = useRef(null);
  const videoRef = useRef(null);
  const loadingTimeoutRef = useRef(null);

  const [content, setContent] = useState({
    video: '/assets/images/home-hero-bg.mp4',
    poster: '/assets/images/home-hero-poster.jpg', // ✅ Fallback poster
    word_1: 'we innovate',
    word_2: 'we research',
    word_3: 'we inspire'
  });
  const [loading, setLoading] = useState(true);
  const [videoLoading, setVideoLoading] = useState(true); // ✅ Video loading state
  const [videoError, setVideoError] = useState(false); // ✅ Video error state
  const [videoUrl, setVideoUrl] = useState('');

  // ✅ Fetch content from API
  useEffect(() => {
    let isMounted = true;

    const fetchContent = async () => {
      try {
        const response = await api.getHeroScrollContent();
        if (response.success && response.data && isMounted) {
          const newContent = {
            ...response.data,
            video: getMediaUrl(response.data.video),
            // ✅ Generate poster from video path or use default
            poster: response.data.poster 
              ? getMediaUrl(response.data.poster)
              : '/assets/images/home-hero-poster.jpg'
          };
          
          setContent(newContent);
          
          const cacheBuster = `?v=${Date.now()}`;
          setVideoUrl(newContent.video + cacheBuster);
          
          console.log('✅ Hero scroll content loaded');
        }
      } catch (error) {
        console.error('❌ Error fetching hero scroll content:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchContent();

    return () => {
      isMounted = false;
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  // ✅ Handle video loading with events
  useEffect(() => {
    if (!videoRef.current || !videoUrl || loading) return;

    const video = videoRef.current;
    let playTimeout;

    const handleCanPlay = () => {
      setVideoLoading(false); // ✅ Video ready
      playTimeout = setTimeout(() => {
        video.play().catch(err => {
          if (err.name !== 'AbortError') {
            console.log('Video autoplay prevented:', err.message);
          }
        });
      }, 100);
    };

    const handleError = () => {
      console.error('❌ Video failed to load');
      setVideoError(true);
      setVideoLoading(false);
    };

    const handleLoadStart = () => {
      setVideoLoading(true); // ✅ Video started loading
    };

    // ✅ Add event listeners
    video.addEventListener('loadstart', handleLoadStart);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('error', handleError);

    // ✅ Load video
    video.load();

    // ✅ Cleanup
    return () => {
      if (playTimeout) clearTimeout(playTimeout);
      video.removeEventListener('loadstart', handleLoadStart);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('error', handleError);
    };
  }, [videoUrl, loading]);

  // ✅ GSAP animations
  useEffect(() => {
    if (loading) return;

    const section = sectionRef.current;
    const line1 = line1Ref.current;
    const line2 = line2Ref.current;
    const line3 = line3Ref.current;

    if (!section || !line1 || !line2 || !line3) return;

    const isMobile = window.innerWidth <= 768;
    const blurAmount = isMobile ? 5 : 10;
    const shouldPin = !isMobile;
    const scrollDistance = isMobile ? 1000 : 3000;

    const line1Chars = line1.querySelectorAll('.dust-char');
    const line2Chars = line2.querySelectorAll('.dust-char');
    const line3Chars = line3.querySelectorAll('.dust-char');

    gsap.set(line1, { opacity: 0, scale: 0.5, filter: `blur(${blurAmount}px)` });
    gsap.set(line2, { opacity: 0, scale: 0.5, filter: `blur(${blurAmount}px)` });
    gsap.set(line3, { opacity: 0, scale: 0.5, filter: `blur(${blurAmount}px)` });

    let pinTrigger;
    if (shouldPin) {
      pinTrigger = ScrollTrigger.create({
        trigger: section,
        start: 'top top',
        end: `+=${scrollDistance}`,
        pin: true,
        pinSpacing: true,
        scrub: 0.2,
        anticipatePin: 1,
      });
    }

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: shouldPin ? 'top top' : 'top center',
        end: shouldPin ? `+=${scrollDistance}` : 'bottom center',
        scrub: 0.2,
      }
    });

    tl.to(line1, {
      opacity: 1,
      scale: 1,
      filter: 'blur(0px)',
      duration: 0.4,
      ease: 'power2.out',
    }, 0)
      .to(line1, { duration: 0.3 })
      // Dust particle explosion out
      .to(line1Chars, {
        opacity: 0,
        scale: () => Math.random() * 1.5 + 0.5,
        x: () => (Math.random() - 0.5) * 600,
        y: () => (Math.random() - 0.5) * 400,
        rotationZ: () => (Math.random() - 0.5) * 180,
        filter: `blur(${blurAmount + 10}px)`,
        duration: 0.6,
        stagger: { amount: 0.2, from: 'center' },
        ease: 'power2.in',
      }, 0.7)
      // Hide line1 container to prevent pointer blocking
      .to(line1, { opacity: 0, duration: 0.01 }, 1.5)
      
      .to(line2, {
        opacity: 1,
        scale: 1,
        filter: 'blur(0px)',
        duration: 0.4,
        ease: 'power2.out',
      }, 1.1)
      .to(line2, { duration: 0.3 })
      // Dust particle explosion out
      .to(line2Chars, {
        opacity: 0,
        scale: () => Math.random() * 1.5 + 0.5,
        x: () => (Math.random() - 0.5) * 600,
        y: () => (Math.random() - 0.5) * 400,
        rotationZ: () => (Math.random() - 0.5) * 180,
        filter: `blur(${blurAmount + 10}px)`,
        duration: 0.6,
        stagger: { amount: 0.2, from: 'center' },
        ease: 'power2.in',
      }, 1.8)
      .to(line2, { opacity: 0, duration: 0.01 }, 2.6)

      .to(line3, {
        opacity: 1,
        scale: 1,
        filter: 'blur(0px)',
        duration: 0.4,
        ease: 'power2.out',
      }, 2.2)
      .to(line3, { duration: 0.4 });

    return () => {
      if (tl) tl.kill();
      if (pinTrigger) pinTrigger.kill();
      ScrollTrigger.getAll().forEach((trigger) => {
        if (trigger.vars.trigger === section) {
          trigger.kill();
        }
      });
    };
  }, [loading]);

  if (loading) {
    return (
      <section className="overlay-text-section">
        <div className="overlay-video-bg" style={{ background: '#191E35' }}></div>
        <div className="overlay-text-content">
          <div className="text-center text-white">
            <div className="spinner-border text-light" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ✅ Helper to split words into animatable dust particles
  const renderWordAsChars = (word) => {
    return word.split('').map((char, index) => (
      <span key={index} className="dust-char" style={{ display: 'inline-block', whiteSpace: 'pre', willChange: 'transform, opacity, filter' }}>
        {char}
      </span>
    ));
  };

return (
  <section ref={sectionRef} className="overlay-text-section">
    {/* ✅ Poster Image (shown while video loads or on error) */}
    <div 
      className={`overlay-poster-image ${!videoLoading && !videoError ? 'hidden' : ''}`}
      style={{ 
        backgroundImage: `url(${content.poster})`,
      }}
    >
      {videoLoading && !videoError && (
        <div className="video-loading-overlay">
          <div className="spinner-border text-light" role="status">
            <span className="visually-hidden">Loading video...</span>
          </div>
          <p className="text-white mt-3">Loading video...</p>
        </div>
      )}
    </div>

    {/* ✅ Video element - FIXED muted attribute */}
    <video
      ref={videoRef}
      className={`overlay-video-bg ${videoLoading || videoError ? 'hidden' : ''}`}
      autoPlay
      loop
      muted={true}  // ✅ Explicitly set to true
      playsInline
      preload="auto"
      poster={content.poster}
      key={videoUrl}
      // ✅ Add these attributes for better compatibility
      webkit-playsinline="true"
      x5-playsinline="true"
      x-webkit-airplay="allow"
    >
      <source src={videoUrl} type="video/mp4" />
      Your browser does not support the video tag.
    </video>

    <div className="overlay-video-overlay"></div>
    
    <div className="overlay-text-content">
      <h2 ref={line1Ref} className="overlay-text-line line-1 text-lowercase">
        <span className="text-light-hq">{renderWordAsChars(content.word_1)}</span>
      </h2>
      <h2 ref={line2Ref} className="overlay-text-line line-2 text-lowercase">
        <span className="text-light-hq">{renderWordAsChars(content.word_2)}</span>
      </h2>
      <h2 ref={line3Ref} className="overlay-text-line line-3 text-lowercase">
        <span className="text-light-hq">{renderWordAsChars(content.word_3)}</span>
      </h2>
    </div>
  </section>
);
};

export default HorizontalScrollText;